import { Play } from "lucide-react";

import { cn } from "@/lib/utils";

import { VS_FONT_FEATURES } from "./constants";
import type { PanelVideo } from "./data";
import { LONDON_PANELS, NY_PANELS } from "./data";

interface VsPanelsProps {
  className?: string;
}

/**
 * "Panels" — a video grid of the panel recordings, sitting between Speakers
 * and Previously in the chapter nav. London's 9 panels (`LONDON_PANELS`,
 * flattened from `SCHEDULE`) always render; a second "New York" grid
 * (`NY_PANELS`) only appears once that array has entries, so the section
 * doesn't show an empty "coming next week" placeholder in the meantime.
 */
export function VsPanels({ className }: VsPanelsProps) {
  return (
    <div
      id="panels"
      style={{ fontFeatureSettings: VS_FONT_FEATURES }}
      className={cn("scroll-mt-[120px] py-[72px] max-md:py-12", className)}
    >
      <h2 className="mb-3 text-[40px] leading-[1.2] font-normal tracking-[-0.04em] text-white max-md:text-[32px]">
        Panels.
      </h2>
      <p className="mb-12 max-w-[640px] text-[14px] leading-[1.6] text-white/70 max-md:mb-9">
        Check out our panels from London, with the recordings from New York
        landing soon.
      </p>

      <PanelGrid panels={LONDON_PANELS} />

      {NY_PANELS.length > 0 ? (
        <div className="mt-12">
          <h3 className="mb-6 text-[20px] font-normal tracking-[-0.01em] text-white">
            New York.
          </h3>
          <PanelGrid panels={NY_PANELS} />
        </div>
      ) : null}
    </div>
  );
}

function PanelGrid({ panels }: { panels: readonly PanelVideo[] }) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {panels.map((panel) => (
        <PanelCard key={panel.title} panel={panel} />
      ))}
    </div>
  );
}

/** One panel card — thumbnail with a play affordance, title, speakers, and
 *  a "Watch on YouTube" link once `videoHref` is set (falls back to
 *  "Recording coming soon" otherwise). */
function PanelCard({ panel }: { panel: PanelVideo }) {
  return (
    <div className="overflow-hidden rounded-2xl bg-[#171717]">
      <div className="relative aspect-video w-full bg-[#1c1c1c]">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={panel.thumbSrc}
          alt=""
          aria-hidden
          loading="lazy"
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover"
        />
        {panel.videoHref ? (
          <a
            href={panel.videoHref}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Watch the panel: ${panel.title}`}
            className={cn(
              "absolute inset-0 flex items-center justify-center bg-black/0 transition-colors motion-reduce:transition-none",
              "hover:bg-black/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-inset"
            )}
          >
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white/90 text-[#0d0d0d]">
              <Play size={20} fill="currentColor" aria-hidden />
            </span>
          </a>
        ) : null}
      </div>
      <div className="p-4">
        {panel.day ? (
          <p className="text-[12px] text-white/50">
            {panel.day} · {panel.time}
          </p>
        ) : null}
        <h3 className="mt-1 text-[16px] font-medium tracking-[-0.01em] text-white">
          {panel.title}
        </h3>
        <p className="mt-1 text-[13px] text-white/55">{panel.speakers}</p>
        {panel.videoHref ? (
          <a
            href={panel.videoHref}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              "mt-3 inline-flex items-center gap-1 text-[14px] text-[#95999d]",
              "transition-colors hover:text-[#f9f9f9] motion-reduce:transition-none",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80 focus-visible:ring-offset-1 focus-visible:ring-offset-[#171717]"
            )}
          >
            Watch on YouTube <span aria-hidden>→</span>
          </a>
        ) : (
          <span className="mt-3 inline-flex items-center gap-1 text-[14px] text-white/40">
            Recording coming soon
          </span>
        )}
      </div>
    </div>
  );
}
